import React, { useEffect } from "react";
import Head from "next/head";
import Script from "next/script";
import dynamic from "next/dynamic";
import styled from "styled-components";
import GlobalStyle from "../style/globalStyle";
import { ContextProvider } from "../Context";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { useRouter } from "next/router";
import { gtmVirtualPageView } from "../utils/cookieUtils";
import { getCookieConsentValue } from "react-cookie-consent";
import CookieConsentPopup from "../components/cookie/CookieConsentPopup";

const Main = dynamic(() => import("../layout/Main"));
const Header = dynamic(() => import("../layout/Header"));
const Footer = dynamic(() => import("../layout/Footer"));

const StyledContainer = styled.div`
  position: relative;
  min-height: 100vh;
`;

const StyledWrapper = styled.div`
  padding-bottom: 8rem;
`;

const version = `1.0.0${process.env.BUILD_VER ? "-" : ""}${process.env.BUILD_VER ?? ""}`

datadogRum.init({
  applicationId: process.env.DD_ID,
  clientToken: process.env.DD_TOKEN,
  site: "datadoghq.eu",
  service: process.env.DD_SERVICE,
  env: process.env.DD_ENV,
  version: version,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: "mask-user-input",
});

datadogRum.startSessionReplayRecording();

datadogLogs.init({
  clientToken: process.env.DD_TOKEN,
  site: "datadoghq.eu",
  service: "application-loan-es",
  env: process.env.DD_ENV,
  version: version,
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
});

datadogLogs.addLoggerGlobalContext("env", process.env.DD_ENV);
datadogLogs.addLoggerGlobalContext("version", version);

export default function App({ Component, pageProps }) {
  const router = useRouter();

  useEffect(() => {
    const mainDataLayer = {
      titleTag: document.title,
      url: router.pathname,
      consentStat: getCookieConsentValue() || "false",
    };

    gtmVirtualPageView(mainDataLayer);
  }, [router.pathname]);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_ID}');
      `}
      </Script>
      <StyledContainer>
        <ContextProvider>
          <GlobalStyle />
          <StyledWrapper>
            <Header />
            <Main>
              <Component {...pageProps} />
              <CookieConsentPopup />
            </Main>
          </StyledWrapper>
          <Footer />
        </ContextProvider>
      </StyledContainer>
    </>
  );
}
